<script setup>
/**
 * The notification bell in the app header.
 *
 * WHAT IT IS FOR
 * --------------
 * Support staff regularly have to change something on a customer's account or
 * one of their jobs — swap a proxy pool, pause a campaign whose card was
 * declined, correct a vote target. Until now the only way the customer heard
 * about it was an email they may never open, so the first they knew was the
 * changed number. This is the in-product half of that: a permanent header
 * control carrying every notice staff wrote about this account, newest first,
 * with the unread ones counted on the badge.
 *
 * WHY IT IS TELEPORTED AND `fixed`
 * --------------------------------
 * Same reason as QuickAdjustPopover.vue, whose approach this follows. The navbar
 * is `fixed ... z-30` with a `backdrop-blur`, and a blur filter creates a
 * containing block for descendants — so an absolutely positioned panel inside it
 * is clipped and re-stacked by the bar itself, and no z-index applied within can
 * escape. Teleporting to <body> and positioning from a measured rect removes
 * both problems at the source.
 *
 * Below `sm` it stops being a floating panel and becomes a full-width sheet
 * under the bar: a 380px popover anchored to a 36px button on a 375px screen has
 * nowhere to go, and reading three-line notices through a panel hanging off the
 * edge of the viewport is not reading.
 *
 * SCROLL REPOSITIONS, IT DOES NOT DISMISS
 * ---------------------------------------
 * Also from QuickAdjustPopover: the panel holds a list somebody is part-way
 * through — possibly several pages deep after "View previous" — and throwing
 * that away because the page moved a few pixels is the same class of bug as
 * clearing a form on a failed save.
 */
import { computed, nextTick, onBeforeUnmount, ref } from 'vue'
import { Bell } from 'lucide-vue-next'
import { mdiClose } from '@mdi/js'
import Icon from '@/components/Icon.vue'
import NotificationItem from '@/components/alerts/NotificationItem.vue'
import { useAlerts } from '@/composables/useAlerts'
import { useCrispChat } from '@/composables/useCrispChat'
import { usePopover } from '@/composables/usePopover'
import { notifyError } from '@/composables/useToast'
import {
  composeChatMessage,
  markVisibleAlertsSeen,
  SEEN_DWELL_MS,
} from '@/components/alerts/alert-presentation'

const { alerts, unreadCount, loading, available, markRead, dismiss } = useAlerts()

/*
 * The dropdown is a SHORT RECENT LIST, not the archive. Paging lives on
 * /notifications now, so this is capped at the first page's worth even when the
 * store holds more (the overview and the bell share one `alerts` array, so
 * pressing "Load more" over there would otherwise silently grow the dropdown
 * into a 60-row scroller).
 */
const DROPDOWN_LIMIT = 10

const recentAlerts = computed(() => alerts.value.slice(0, DROPDOWN_LIMIT))

/* `openCrispChat` only ever REQUESTS a chat — the confirmation dialog in App.vue
   is the single path that actually opens one, and the prefilled message is
   carried through it. Deliberately not bypassed: starting a real conversation
   with a human is not something a notification row gets to do unprompted. */
const { openCrispChat } = useCrispChat()

const isOpen = ref(false)

/**
 * Which row is showing its full body. ONE at a time, and owned here rather than
 * inside each row: with several open at once the panel becomes a wall of text
 * that has to be scrolled past to reach anything else, and the point of the
 * dropdown is that it can be skimmed.
 *
 * Reset on close, so reopening the bell is always the short list again.
 */
const expandedId = ref(null)

const onToggleExpand = (id) => {
  expandedId.value = expandedId.value === id ? null : id
}

const triggerRef = ref(null)
const panelRef = ref(null)
const listRef = ref(null)

/* The badge stops counting and starts saying "lots". Past this the exact number
   is noise, and three digits do not fit on a dot pinned to a 36px button. */
const BADGE_CAP = 9

const badgeLabel = computed(() =>
  unreadCount.value > BADGE_CAP ? `${BADGE_CAP}+` : String(unreadCount.value)
)

const bellLabel = computed(() => {
  if (unreadCount.value === 0) return 'Notifications'
  return `Notifications, ${unreadCount.value} unread`
})

/* ── Placement ───────────────────────────────────────────────────────────── */

const PANEL_WIDTH = 380
const GAP = 8
const MARGIN = 8

const sheetQuery =
  typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia('(max-width: 639px)')
    : null
const isSheet = ref(sheetQuery ? sheetQuery.matches : false)

const onSheetQueryChange = (event) => {
  isSheet.value = event.matches
  if (isOpen.value) nextTick(place)
}

const position = ref({ top: 0, left: 0 })

const clamp = (value, min, max) => Math.min(Math.max(value, min), Math.max(min, max))

/**
 * Measure the bell and place the panel under it, right-aligned. The bell sits in
 * the right-hand header cluster, so opening leftwards is the only direction with
 * room; `clamp` then keeps it on screen on narrow desktop windows.
 */
const place = () => {
  if (isSheet.value) return
  const trigger = triggerRef.value
  if (!trigger) return

  const rect = trigger.getBoundingClientRect()
  const vw = window.innerWidth

  position.value = {
    top: rect.bottom + GAP,
    left: clamp(rect.right - PANEL_WIDTH, MARGIN, Math.max(MARGIN, vw - PANEL_WIDTH - MARGIN)),
  }
}

const panelStyle = computed(() => {
  if (isSheet.value) return {}
  return {
    top: `${position.value.top}px`,
    left: `${position.value.left}px`,
    width: `${PANEL_WIDTH}px`,
  }
})

/* ── "Actually seen" ─────────────────────────────────────────────────────────
 * A row counts as read when it has been ON SCREEN inside the scroller for a
 * moment — not when the poll fetched it, and not simply because the dropdown was
 * opened over a list of thirty. Marking everything read on open would mean one
 * stray click on the bell wipes the unread state off notices the reader never
 * scrolled to, which is precisely the state the badge exists to preserve.
 *
 * The dwell also protects a fast scroll: rows that flick past on the way to the
 * bottom are not "seen", and the timer restarts on every scroll so only where
 * the reader actually stops counts.
 *
 * NotificationItem additionally emits `seen` on hover, on focus and on expand,
 * which covers pointer and keyboard readers ahead of the timer.
 *
 * The visibility test itself lives in alert-presentation.js and is shared with
 * the overview page, so "seen" means the same thing on both surfaces. Here the
 * scroller is the panel's own list element; there it is the window.
 */
let seenTimer = null
let scrollFrame = 0

const markVisibleSeen = () => {
  if (!isOpen.value) return
  markVisibleAlertsSeen(listRef.value, markRead, listRef.value)
}

const scheduleSeen = () => {
  clearTimeout(seenTimer)
  seenTimer = setTimeout(markVisibleSeen, SEEN_DWELL_MS)
}

const onListScroll = () => {
  scheduleSeen()
}

/* ── Open / close ────────────────────────────────────────────────────────── */

const close = ({ restoreFocus = true } = {}) => {
  if (!isOpen.value) return
  isOpen.value = false
  unbind()
  clearTimeout(seenTimer)
  // Reopening the bell is always the short skimmable list again.
  expandedId.value = null
  // Focus goes back to the bell, not to the top of the document — a keyboard
  // user closing the panel is still standing in the header. Not when the panel
  // closed because the reader clicked elsewhere or opened another header
  // panel: focus belongs where they went.
  if (restoreFocus) triggerRef.value?.focus()
}

/* One header popover open at a time; focus moves into the panel on open, and
   Tab past either end closes it (see composables/usePopover.js). The panel is
   teleported to the end of <body>, so without this a keyboard user who opened
   it could only reach it by tabbing through the entire page. */
const { panelId, onKeydown: onPanelKeydown } = usePopover({
  name: 'notifications',
  isOpen,
  close,
  trigger: triggerRef,
  panel: panelRef,
})

const onDocumentPointerDown = (event) => {
  const target = event.target
  if (!(target instanceof Node)) return
  if (panelRef.value && panelRef.value.contains(target)) return
  // The bell toggles the panel itself; dismissing here as well would close and
  // reopen it in one click, so the bell would look inert.
  if (triggerRef.value && triggerRef.value.contains(target)) return
  close({ restoreFocus: false })
}

const onDocumentKeydown = (event) => {
  if (event.key === 'Escape' || event.key === 'Esc') {
    event.stopPropagation()
    close()
  }
}

const onScrollOrResize = () => {
  if (!isOpen.value || isSheet.value) return
  if (scrollFrame) return
  scrollFrame = requestAnimationFrame(() => {
    scrollFrame = 0
    if (isOpen.value) place()
  })
}

const bind = () => {
  document.addEventListener('pointerdown', onDocumentPointerDown, true)
  document.addEventListener('keydown', onDocumentKeydown, true)
  window.addEventListener('scroll', onScrollOrResize, true)
  window.addEventListener('resize', onScrollOrResize)
}

const unbind = () => {
  document.removeEventListener('pointerdown', onDocumentPointerDown, true)
  document.removeEventListener('keydown', onDocumentKeydown, true)
  window.removeEventListener('scroll', onScrollOrResize, true)
  window.removeEventListener('resize', onScrollOrResize)
  if (scrollFrame) {
    cancelAnimationFrame(scrollFrame)
    scrollFrame = 0
  }
}

const toggle = async () => {
  if (isOpen.value) {
    close()
    return
  }

  isOpen.value = true
  bind()
  await nextTick()
  place()
  scheduleSeen()
}

/* ── Row actions ─────────────────────────────────────────────────────────── */

const onDismiss = async (alert) => {
  const result = await dismiss(alert.id)
  if (result.ok) return

  /*
   * The one failure in this component that MUST be loud. The row has already
   * gone from the list (that is the optimism), the composable has put it back,
   * and without this the customer would have watched a notice disappear, believe
   * it dealt with, and meet it again on the next load with no idea why.
   */
  notifyError("That notification is still there", {
    detail: `${result.message} It has been put back in your notifications.`,
    dedupeKey: `alert-dismiss-failed:${alert.id}`,
  })
}

/* The message support receives is composed in alert-presentation.js, shared with
   the overview page: whichever screen the customer pressed the button on, the
   agent has to open the same block of context. */
const onDiscuss = (alert) => {
  // Taking a notice to support is about as unambiguous a "seen" as exists.
  markRead(alert.id)
  openCrispChat(composeChatMessage(alert), { offerTutorial: false })
  // The Crisp window and its confirmation dialog own the screen from here; a
  // dropdown left open behind them would be two panels deep.
  close()
}

if (sheetQuery) {
  if (sheetQuery.addEventListener) sheetQuery.addEventListener('change', onSheetQueryChange)
  else if (sheetQuery.addListener) sheetQuery.addListener(onSheetQueryChange)
}

onBeforeUnmount(() => {
  unbind()
  clearTimeout(seenTimer)
  if (!sheetQuery) return
  if (sheetQuery.removeEventListener) sheetQuery.removeEventListener('change', onSheetQueryChange)
  else if (sheetQuery.removeListener) sheetQuery.removeListener(onSheetQueryChange)
})
</script>

<template>
  <!-- `available: false` means the backend cannot store alerts at all yet. The
       bell does not render — not a disabled bell, not an empty one. A permanent
       header control that can never contain anything is a promise the product
       cannot keep. -->
  <div v-if="available" class="relative">
    <!-- Sized and styled from the dark-mode toggle beside it, deliberately to
         the class: two icon buttons in one header cluster that disagree by two
         pixels of radius is the kind of thing nobody names but everybody sees. -->
    <button
      ref="triggerRef"
      type="button"
      class="relative inline-flex items-center justify-center w-9 h-9 min-w-[2.75rem] min-h-[2.75rem] sm:min-w-0 sm:min-h-0 rounded-xl border transition-colors
             border-gray-200 bg-white text-gray-500 hover:text-brand-600 hover:border-brand-300 hover:bg-brand-50
             dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400 dark:hover:text-brand-300 dark:hover:bg-gray-700
             focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2
             dark:focus-visible:ring-offset-gray-800"
      :class="isOpen ? 'border-brand-300 bg-brand-50 text-brand-600 dark:bg-gray-700 dark:text-brand-300' : ''"
      :aria-label="bellLabel"
      :title="bellLabel"
      aria-haspopup="dialog"
      :aria-expanded="isOpen"
      :aria-controls="isOpen ? panelId : undefined"
      @click="toggle"
    >
      <icon :path="Bell" :size="18" w="w-5" h="h-5" />

      <!-- The count is on the badge for sighted readers and in the button's
           aria-label for everyone else, so it is never announced twice. -->
      <span
        v-if="unreadCount > 0"
        class="absolute -top-1 -right-1 inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-red-600 px-1 text-3xs font-semibold leading-none text-white ring-2 ring-white dark:bg-red-500 dark:ring-gray-800"
        aria-hidden="true"
      >
        {{ badgeLabel }}
      </span>
    </button>

    <teleport to="body">
      <!-- The scrim exists only in sheet mode. On desktop the page behind an
           anchored dropdown stays perfectly usable, so dimming it would claim a
           modality the panel does not have. -->
      <div
        v-if="isOpen && isSheet"
        class="fixed inset-0 z-[9998] bg-gray-900 bg-opacity-40"
        @click="close({ restoreFocus: false })"
      />

      <div
        v-if="isOpen"
        :id="panelId"
        ref="panelRef"
        role="dialog"
        aria-label="Recent notifications"
        class="fixed z-[9999] flex flex-col border border-gray-200 bg-white shadow-card dark:border-gray-700 dark:bg-gray-800"
        :class="isSheet
          ? 'inset-x-0 top-14 bottom-0 rounded-t-xl pb-[env(safe-area-inset-bottom)]'
          : 'rounded-xl'"
        :style="panelStyle"
        @keydown="onPanelKeydown"
      >
        <div
          class="flex flex-shrink-0 items-center justify-between gap-2 border-b border-gray-100 px-4 py-3 dark:border-gray-700"
        >
          <p class="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Recent notifications
          </p>
          <button
            type="button"
            class="-mr-1 inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 sm:h-8 sm:w-8 dark:hover:bg-gray-700 dark:hover:text-gray-100"
            aria-label="Close notifications"
            @click="close"
          >
            <icon :path="mdiClose" :size="18" w="w-5" h="h-5" />
          </button>
        </div>

        <!-- The scroller. `max-h-[60vh]` on desktop rather than a pixel height so
             the panel never outgrows a short laptop window; in sheet mode the
             panel is already bounded by `top-14 bottom-0`, so the list just
             takes what is left with `flex-1`. -->
        <div
          ref="listRef"
          class="flex-1 overflow-y-auto overflow-x-hidden"
          :class="isSheet ? '' : 'max-h-[60vh]'"
          @scroll="onListScroll"
        >
          <p
            v-if="loading && alerts.length === 0"
            class="px-4 py-8 text-center text-sm text-gray-400 dark:text-gray-500"
          >
            Loading…
          </p>

          <!-- Quiet, one line, no illustration. An empty notification list is
               the normal, good state — nothing has gone wrong on the account —
               so it should not look like a feature waiting to be used. -->
          <p
            v-else-if="recentAlerts.length === 0"
            class="px-4 py-8 text-center text-sm text-gray-400 dark:text-gray-500"
          >
            No notifications
          </p>

          <ul v-else class="divide-y divide-gray-100 dark:divide-gray-700">
            <notification-item
              v-for="alert in recentAlerts"
              :key="alert.id"
              :alert="alert"
              :expanded="expandedId === alert.id"
              @dismiss="onDismiss"
              @discuss="onDiscuss"
              @seen="markRead"
              @toggle="onToggleExpand"
              @navigate="close"
            />
          </ul>
        </div>

        <!-- Pinned to the bottom, outside the scroller, so it is reachable
             without scrolling to the end of the list first.

             This REPLACED a "View previous" button that paged older notices into
             the dropdown. Two ways to reach the same history is one too many,
             and the panel is the wrong shape for reading an archive: a 380px
             column with a 60vh cap. Paging now lives on /notifications, where
             every body is open and there is room to read. -->
        <div
          v-if="recentAlerts.length > 0"
          class="flex-shrink-0 border-t border-gray-100 px-2 py-2 dark:border-gray-700"
        >
          <router-link
            to="/notifications"
            class="inline-flex min-h-[2.25rem] w-full items-center justify-center rounded-lg text-sm font-semibold text-brand-600 transition-colors hover:bg-brand-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:text-brand-400 dark:hover:bg-gray-700"
            @click="close"
          >
            View all notifications
          </router-link>
        </div>
      </div>
    </teleport>
  </div>
</template>
